import { ApplicationController } from "../../support/application-controller";

function escapeHtml(str) {
    if (str === null || str === undefined) return '';
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(String(str)));
    return div.innerHTML;
}

function formatMappings(mappings) {
    if (!mappings || mappings.length === 0) {
        return '<span class="hubspot-tools__mapping--unmapped">unmapped</span>';
    }
    return mappings.map(function(m) {
        return '<div class="hubspot-tools__mapping">' +
            '<span class="hubspot-tools__mapping-type">' + escapeHtml(m.external_source_type) + '</span>: ' +
            '<code>' + escapeHtml(m.external_source_id) + '</code>' +
            '</div>';
    }).join('');
}

function hubspotTypeForMapping(externalSourceType) {
    // Maps an ExternalMapping.external_source_type to the object-type
    // slug accepted by the /hubspot_record endpoint.
    switch (externalSourceType) {
        case 'hubspot_contact': return 'contact';
        case 'hubspot_deal':    return 'contact'; // deals aren't fetched here; fall back to contact view. See known limitations.
        case 'hubspot_company': return 'company';
        case 'Hubspot':         return 'subscription';
        default: return null;
    }
}

/**
 * Stimulus controller powering the Users / Organizations / Subscriptions
 * tabs on the HubSpot Tools page. All three tabs render the same markup
 * and share this controller; data-kind / data-local-type / data-hubspot-type
 * attributes on the root element pick the behavior.
 */
export default class extends ApplicationController {
    static targets = [
        "filterInput",
        "limitInput",
        "reloadButton",
        "statusMessage",
        "tablePanel",
        "tableBody",
        "modal",
        "modalTitle",
        "modalBody",
        "modalFooter"
    ];

    connect() {
        this.rows = [];
        this.loaded = false;
        this.boundTabActivated = this.onTabActivated.bind(this);
        // The pane is initially display:none; wait for the toggle controller
        // to notify us so we only hit the API when actually viewed.
        var toggleRoot = this.element.closest('[data-controller~="admin--hubspot-tools-toggle"]');
        if (toggleRoot) {
            toggleRoot.addEventListener('hubspot-tools:tab-activated', this.boundTabActivated);
        }
    }

    disconnect() {
        var toggleRoot = this.element.closest('[data-controller~="admin--hubspot-tools-toggle"]');
        if (toggleRoot && this.boundTabActivated) {
            toggleRoot.removeEventListener('hubspot-tools:tab-activated', this.boundTabActivated);
        }
    }

    onTabActivated(event) {
        var myKind = this.element.getAttribute('data-kind');
        if (event && event.detail && event.detail.tab === myKind && !this.loaded) {
            this.loadRows();
        }
    }

    reload(event) {
        if (event) event.preventDefault();
        this.loadRows();
    }

    loadRows() {
        var self = this;
        var limit = this.hasLimitInputTarget ? parseInt(this.limitInputTarget.value, 10) : 50;
        if (!limit || limit < 1) limit = 50;

        var url = this.element.getAttribute('data-list-url') + '?limit=' + encodeURIComponent(limit);
        this.setStatus('Loading…');
        if (this.hasReloadButtonTarget) this.reloadButtonTarget.disabled = true;

        fetch(url, {
            headers: { "Accept": "application/json", "X-CSRF-Token": this.getCSRFToken() },
            credentials: "same-origin"
        })
            .then(function(r) { return r.json(); })
            .then(function(data) {
                if (self.hasReloadButtonTarget) self.reloadButtonTarget.disabled = false;
                if (data.success) {
                    self.rows = data.rows || [];
                    self.loaded = true;
                    self.renderTable();
                    self.setStatus('Loaded ' + self.rows.length + ' of ' + data.total + ' total.');
                } else {
                    self.setStatus('Failed: ' + (data.error || 'Unknown error'));
                }
            })
            .catch(function(err) {
                if (self.hasReloadButtonTarget) self.reloadButtonTarget.disabled = false;
                self.setStatus('Error: ' + err.message);
            });
    }

    renderTable() {
        if (!this.hasTablePanelTarget || !this.hasTableBodyTarget) return;
        this.tablePanelTarget.style.display = 'block';

        var html = '';
        var kind = this.element.getAttribute('data-kind');
        for (var i = 0; i < this.rows.length; i++) {
            var r = this.rows[i];
            var displayName = escapeHtml(r.name || '(no name)');
            var email = r.email ? '<br><span class="hubspot-tools__muted">' + escapeHtml(r.email) + '</span>' : '';
            var secondary = r.secondary ? escapeHtml(r.secondary) : '';
            if (r.status) {
                secondary += (secondary ? ' · ' : '') + escapeHtml(r.status);
            }

            html += '<tr data-row-index="' + i + '">' +
                '<td><code>' + escapeHtml(r.local_id) + '</code></td>' +
                '<td>' + displayName + email + '</td>' +
                '<td>' + secondary + '</td>' +
                '<td>' + formatMappings(r.mappings) + '</td>' +
                '<td>' +
                    '<button type="button" class="button-flat hubspot-tools__action-btn"' +
                        ' data-action="click->admin--hubspot-external-mappings#verify"' +
                        ' data-row-index="' + i + '"' +
                        (r.mappings && r.mappings.length > 0 ? '' : ' disabled') + '>' +
                        'Verify' +
                    '</button> ' +
                    '<button type="button" class="button-flat hubspot-tools__action-btn"' +
                        ' data-action="click->admin--hubspot-external-mappings#openSearch"' +
                        ' data-row-index="' + i + '">' +
                        'Search HubSpot' +
                    '</button> ' +
                    '<button type="button" class="button-flat hubspot-tools__action-btn"' +
                        ' data-action="click->admin--hubspot-external-mappings#openSetId"' +
                        ' data-row-index="' + i + '">' +
                        'Set Record ID' +
                    '</button>' +
                '</td>' +
                '</tr>';
        }
        this.tableBodyTarget.innerHTML = html;
        this.applyFilter();
        // Suppress linter for unused kind - reserved for future per-kind display.
        void kind;
    }

    applyFilter() {
        if (!this.hasTableBodyTarget) return;
        var filter = this.hasFilterInputTarget ? this.filterInputTarget.value.toLowerCase() : '';
        var trs = this.tableBodyTarget.querySelectorAll('tr');
        for (var i = 0; i < trs.length; i++) {
            var idx = parseInt(trs[i].getAttribute('data-row-index'), 10);
            var r = this.rows[idx];
            if (!filter) {
                trs[i].style.display = '';
                continue;
            }
            var hay = ((r.name || '') + ' ' + (r.email || '') + ' ' + (r.secondary || '')).toLowerCase();
            trs[i].style.display = hay.indexOf(filter) >= 0 ? '' : 'none';
        }
    }

    // ---- Actions on a row ----

    verify(event) {
        if (event) event.preventDefault();
        var idx = parseInt(event.currentTarget.getAttribute('data-row-index'), 10);
        var row = this.rows[idx];
        if (!row || !row.mappings || row.mappings.length === 0) return;

        // Prefer the mapping that matches the primary hubspot-type for this tab.
        var primaryType = this.element.getAttribute('data-hubspot-type');
        var mapping = null;
        for (var i = 0; i < row.mappings.length; i++) {
            if (hubspotTypeForMapping(row.mappings[i].external_source_type) === primaryType) {
                mapping = row.mappings[i];
                break;
            }
        }
        mapping = mapping || row.mappings[0];
        var hsType = hubspotTypeForMapping(mapping.external_source_type) || primaryType;

        this.openModal('Verify mapping — ' + (row.name || '#' + row.local_id));
        this.renderModalBody(
            '<p>Fetching HubSpot ' + escapeHtml(hsType) + ' <code>' + escapeHtml(mapping.external_source_id) + '</code>…</p>'
        );

        var self = this;
        this.fetchHubspotRecord(hsType, mapping.external_source_id)
            .then(function(data) {
                if (data.error) {
                    self.renderModalBody('<p class="hubspot-tools__error-summary">' + escapeHtml(data.error) + '</p>');
                } else {
                    self.renderVerifySideBySide(row, mapping, data.record);
                }
            })
            .catch(function(err) {
                self.renderModalBody('<p class="hubspot-tools__error-summary">' + escapeHtml(err.message) + '</p>');
            });
    }

    openSearch(event) {
        if (event) event.preventDefault();
        var idx = parseInt(event.currentTarget.getAttribute('data-row-index'), 10);
        this.currentRowIndex = idx;
        var row = this.rows[idx];
        this.openModal('Search HubSpot for ' + (row.name || '#' + row.local_id));
        var label = this.modalBodyTarget.getAttribute('data-search-label') || 'Search…';

        // Pre-populate the search input with the most useful value for the
        // tab. Users tab → email (the field HubSpot search filters on);
        // orgs/subs → row's display name. Data is already on the client (set
        // by admin_hubspot_external_mappings_controller.rb's row_for_*), so
        // there is no extra round-trip.
        var kind = this.element.getAttribute('data-kind');
        var prefill = '';
        if (kind === 'users') {
            prefill = row.email || '';
        } else if (kind === 'organizations' || kind === 'subscriptions') {
            prefill = row.name || '';
        }

        this.renderModalBody(
            '<div class="hubspot-tools__control-group">' +
                '<label class="hubspot-tools__label">Query:</label>' +
                '<input type="text" class="hubspot-tools__input" data-em-role="search-input" value="' + escapeHtml(prefill) + '" placeholder="' + escapeHtml(label) + '">' +
            '</div>' +
            '<div class="hubspot-tools__actions" style="margin: 12px 0;">' +
                '<button type="button" class="button-flat button-flat--primary" data-em-role="search-go">Search</button>' +
            '</div>' +
            '<div data-em-role="search-results"></div>'
        );
        this.setModalFooter('<button type="button" class="button-flat" data-action="click->admin--hubspot-external-mappings#closeModal">Cancel</button>');

        var self = this;
        var input = this.modalBodyTarget.querySelector('[data-em-role="search-input"]');
        if (input) {
            input.addEventListener('keypress', function(e) {
                if (e.key === 'Enter') { e.preventDefault(); self.runSearch(); }
            });
            input.focus();
            // Auto-run when we have a prefill so the admin sees results
            // immediately without an extra click.
            if (prefill) self.runSearch();
        }
        var btn = this.modalBodyTarget.querySelector('[data-em-role="search-go"]');
        if (btn) btn.addEventListener('click', function() { self.runSearch(); });
    }

    runSearch() {
        var self = this;
        var input = this.modalBodyTarget.querySelector('[data-em-role="search-input"]');
        var resultsEl = this.modalBodyTarget.querySelector('[data-em-role="search-results"]');
        if (!input || !resultsEl) return;
        var query = input.value;
        if (!query) return;

        resultsEl.innerHTML = '<p class="hubspot-tools__muted">Searching…</p>';

        var hsType = this.element.getAttribute('data-hubspot-type');
        var url = this.element.getAttribute('data-search-url') +
            '?type=' + encodeURIComponent(hsType) +
            '&query=' + encodeURIComponent(query);

        fetch(url, {
            headers: { "Accept": "application/json", "X-CSRF-Token": this.getCSRFToken() },
            credentials: "same-origin"
        })
            .then(function(r) { return r.json(); })
            .then(function(data) {
                if (data.error) {
                    resultsEl.innerHTML = '<p class="hubspot-tools__error-summary">' + escapeHtml(data.error) + '</p>';
                    return;
                }
                var results = data.results || [];
                if (results.length === 0) {
                    resultsEl.innerHTML = '<p class="hubspot-tools__muted">No matches.</p>';
                    return;
                }
                var html = '<table class="hubspot-tools__results-table"><thead><tr><th>Record</th><th>Details</th><th></th></tr></thead><tbody>';
                for (var i = 0; i < results.length; i++) {
                    var r = results[i];
                    html += '<tr>' +
                        '<td><code>' + escapeHtml(r.id) + '</code></td>' +
                        '<td>' +
                            (r.name ? escapeHtml(r.name) + '<br>' : '') +
                            (r.email ? '<span class="hubspot-tools__muted">' + escapeHtml(r.email) + '</span><br>' : '') +
                            (r.domain ? '<span class="hubspot-tools__muted">' + escapeHtml(r.domain) + '</span>' : '') +
                            (r.status ? '<span class="hubspot-tools__muted">' + escapeHtml(r.status) + '</span>' : '') +
                        '</td>' +
                        '<td><button type="button" class="button-flat" data-em-role="pick-result" data-rid="' + escapeHtml(r.id) + '">Select</button></td>' +
                        '</tr>';
                }
                html += '</tbody></table>';
                resultsEl.innerHTML = html;

                var buttons = resultsEl.querySelectorAll('[data-em-role="pick-result"]');
                var self2 = self;
                buttons.forEach(function(b) {
                    b.addEventListener('click', function() {
                        self2.proposeMapping(b.getAttribute('data-rid'));
                    });
                });
            })
            .catch(function(err) {
                resultsEl.innerHTML = '<p class="hubspot-tools__error-summary">' + escapeHtml(err.message) + '</p>';
            });
    }

    openSetId(event) {
        if (event) event.preventDefault();
        var idx = parseInt(event.currentTarget.getAttribute('data-row-index'), 10);
        this.currentRowIndex = idx;
        var row = this.rows[idx];
        this.openModal('Set HubSpot Record ID — ' + (row.name || '#' + row.local_id));
        this.renderModalBody(
            '<div class="hubspot-tools__control-group">' +
                '<label class="hubspot-tools__label">HubSpot record ID:</label>' +
                '<input type="text" class="hubspot-tools__input" data-em-role="set-id-input" placeholder="e.g. 12345">' +
                '<p class="hubspot-tools__muted" style="margin-top:4px;font-size:12px;">We will fetch the record for confirmation before writing the mapping.</p>' +
            '</div>' +
            '<div class="hubspot-tools__actions" style="margin: 12px 0;">' +
                '<button type="button" class="button-flat button-flat--primary" data-em-role="set-id-go">Preview</button>' +
            '</div>'
        );
        this.setModalFooter('<button type="button" class="button-flat" data-action="click->admin--hubspot-external-mappings#closeModal">Cancel</button>');

        var self = this;
        var input = this.modalBodyTarget.querySelector('[data-em-role="set-id-input"]');
        if (input) input.focus();
        var btn = this.modalBodyTarget.querySelector('[data-em-role="set-id-go"]');
        if (btn) {
            btn.addEventListener('click', function() {
                var v = input ? input.value.trim() : '';
                if (v) self.proposeMapping(v);
            });
        }
    }

    proposeMapping(externalId) {
        var self = this;
        var row = this.rows[this.currentRowIndex];
        var hsType = this.element.getAttribute('data-hubspot-type');
        this.renderModalBody('<p>Fetching HubSpot ' + escapeHtml(hsType) + ' <code>' + escapeHtml(externalId) + '</code> for confirmation…</p>');
        this.setModalFooter('<button type="button" class="button-flat" data-action="click->admin--hubspot-external-mappings#closeModal">Cancel</button>');

        this.fetchHubspotRecord(hsType, externalId)
            .then(function(data) {
                if (data.error) {
                    self.renderModalBody('<p class="hubspot-tools__error-summary">' + escapeHtml(data.error) + '</p>');
                } else {
                    self.renderConfirm(row, data.record);
                }
            })
            .catch(function(err) {
                self.renderModalBody('<p class="hubspot-tools__error-summary">' + escapeHtml(err.message) + '</p>');
            });
    }

    confirmMapping() {
        var row = this.rows[this.currentRowIndex];
        var hsType = this.element.getAttribute('data-hubspot-type');
        var externalType = this.externalTypeForHubspotType(hsType);
        var localType = this.element.getAttribute('data-local-type');
        var externalId = this.proposedExternalId;

        var body = {
            local_type: localType,
            local_id: row.local_id,
            external_type: externalType,
            external_id: externalId
        };

        this.renderModalBody('<p>Writing mapping and syncing to HubSpot…</p>');
        this.setModalFooter('');

        var self = this;
        fetch(this.element.getAttribute('data-upsert-url'), {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "X-CSRF-Token": this.getCSRFToken()
            },
            credentials: "same-origin",
            body: JSON.stringify(body)
        })
            .then(function(r) { return r.json().then(function(j) { return { status: r.status, body: j }; }); })
            .then(function(res) {
                if (res.body.success) {
                    self.renderModalBody(self.renderSuccess(res.body));
                    self.setModalFooter('<button type="button" class="button-flat button-flat--primary" data-action="click->admin--hubspot-external-mappings#closeAndReload">Close &amp; Reload</button>');
                } else {
                    self.renderModalBody('<p class="hubspot-tools__error-summary">' + escapeHtml(res.body.error || 'Unknown error') + '</p>');
                    self.setModalFooter('<button type="button" class="button-flat" data-action="click->admin--hubspot-external-mappings#closeModal">Close</button>');
                }
            })
            .catch(function(err) {
                self.renderModalBody('<p class="hubspot-tools__error-summary">' + escapeHtml(err.message) + '</p>');
                self.setModalFooter('<button type="button" class="button-flat" data-action="click->admin--hubspot-external-mappings#closeModal">Close</button>');
            });
    }

    closeAndReload() {
        this.closeModal();
        this.loadRows();
    }

    // ---- Rendering helpers ----

    renderSuccess(body) {
        var m = body.mapping || {};
        var s = body.sync || {};
        var html = '<p class="hubspot-tools__status-badge hubspot-tools__status-badge--created">' + escapeHtml(m.action || 'done') + '</p>';
        html += '<p>Mapping id: <code>' + escapeHtml(m.mapping_id) + '</code></p>';
        if (m.previous_external_id) {
            html += '<p>Previous HubSpot id: <code>' + escapeHtml(m.previous_external_id) + '</code></p>';
        }
        html += '<p>New HubSpot id: <code>' + escapeHtml(m.external_source_id) + '</code></p>';
        if (s.attempted) {
            if (s.success) {
                html += '<p class="hubspot-tools__status-badge hubspot-tools__status-badge--created">Sync succeeded</p>';
                if (s.attributes && s.attributes.length) {
                    html += '<p>Synced attributes: ' + escapeHtml(s.attributes.join(', ')) + '</p>';
                }
            } else {
                html += '<p class="hubspot-tools__status-badge hubspot-tools__status-badge--error">Sync failed</p>';
                if (s.error) html += '<p class="hubspot-tools__error-summary">' + escapeHtml(s.error) + '</p>';
            }
        } else {
            html += '<p class="hubspot-tools__muted">Sync skipped: ' + escapeHtml(s.reason || 'unknown') + '</p>';
        }
        return html;
    }

    renderVerifySideBySide(localRow, mapping, hsRecord) {
        var html = '<div class="hubspot-tools__verify-columns">';
        html += '<div class="hubspot-tools__verify-col">';
        html += '<h4>Local — ' + escapeHtml(this.element.getAttribute('data-local-type')) + '</h4>';
        html += this.keyValueTable([
            ['ID', localRow.local_id],
            ['Name', localRow.name],
            ['Email', localRow.email],
            ['Secondary', localRow.secondary]
        ]);
        html += '</div>';
        html += '<div class="hubspot-tools__verify-col">';
        html += '<h4>HubSpot — ' + escapeHtml(hsRecord.type) + '</h4>';
        html += this.keyValueTable(this.hubspotFieldsFor(hsRecord));
        html += '</div>';
        html += '</div>';
        html += '<p class="hubspot-tools__muted" style="margin-top:8px;">' +
            'Mapping: <code>' + escapeHtml(mapping.external_source_type) + '</code> → <code>' + escapeHtml(mapping.external_source_id) + '</code>' +
            '</p>';

        this.renderModalBody(html);
        this.setModalFooter('<button type="button" class="button-flat" data-action="click->admin--hubspot-external-mappings#closeModal">Close</button>');
    }

    renderConfirm(localRow, hsRecord) {
        this.proposedExternalId = hsRecord.id;

        var html = '<div class="hubspot-tools__verify-columns">';
        html += '<div class="hubspot-tools__verify-col">';
        html += '<h4>Local — ' + escapeHtml(this.element.getAttribute('data-local-type')) + '</h4>';
        html += this.keyValueTable([
            ['ID', localRow.local_id],
            ['Name', localRow.name],
            ['Email', localRow.email]
        ]);
        html += '</div>';
        html += '<div class="hubspot-tools__verify-col">';
        html += '<h4>HubSpot — ' + escapeHtml(hsRecord.type) + ' (proposed)</h4>';
        html += this.keyValueTable(this.hubspotFieldsFor(hsRecord));
        html += '</div>';
        html += '</div>';
        html += '<p class="hubspot-tools__warning" style="display:block;margin-top:12px;">' +
            'Confirming will overwrite any existing mapping of this type and immediately push TractIQ properties to HubSpot.' +
            '</p>';

        this.renderModalBody(html);
        this.setModalFooter(
            '<button type="button" class="button-flat" data-action="click->admin--hubspot-external-mappings#closeModal">Cancel</button>' +
            '<button type="button" class="button-flat button-flat--primary" data-action="click->admin--hubspot-external-mappings#confirmMapping">Confirm &amp; Sync</button>'
        );
    }

    keyValueTable(pairs) {
        var html = '<table class="hubspot-tools__stats-table">';
        for (var i = 0; i < pairs.length; i++) {
            var k = pairs[i][0];
            var v = pairs[i][1];
            if (v === null || v === undefined || v === '') continue;
            html += '<tr><td><strong>' + escapeHtml(k) + '</strong></td><td>' + escapeHtml(v) + '</td></tr>';
        }
        html += '</table>';
        return html;
    }

    hubspotFieldsFor(rec) {
        var pairs = [['ID', rec.id]];
        if (rec.type === 'contact') {
            pairs.push(['Email', rec.email]);
            pairs.push(['Name', rec.name]);
            pairs.push(['Company', rec.company]);
            pairs.push(['Lifecycle stage', rec.lifecycle_stage]);
            pairs.push(['TractIQ user id', rec.tractiq_user_id]);
        } else if (rec.type === 'company') {
            pairs.push(['Name', rec.name]);
            pairs.push(['Domain', rec.domain]);
            pairs.push(['Industry', rec.industry]);
            pairs.push(['Lifecycle stage', rec.lifecycle_stage]);
            pairs.push(['TractIQ organization id', rec.tractiq_organization_id]);
        } else if (rec.type === 'subscription') {
            pairs.push(['Name', rec.name]);
            pairs.push(['Status', rec.status]);
            pairs.push(['Contact email', rec.contact_email]);
            pairs.push(['Next payment amount', rec.next_payment_amount]);
            pairs.push(['TractIQ subscription id', rec.tractiq_subscription_id]);
        }
        return pairs;
    }

    externalTypeForHubspotType(hsType) {
        switch (hsType) {
            case 'contact': return 'hubspot_contact';
            case 'company': return 'hubspot_company';
            case 'subscription': return 'Hubspot';
            default: return hsType;
        }
    }

    fetchHubspotRecord(type, id) {
        var url = this.element.getAttribute('data-record-url') +
            '?type=' + encodeURIComponent(type) +
            '&id=' + encodeURIComponent(id);
        return fetch(url, {
            headers: { "Accept": "application/json", "X-CSRF-Token": this.getCSRFToken() },
            credentials: "same-origin"
        }).then(function(r) { return r.json(); });
    }

    // ---- Modal plumbing ----

    openModal(title) {
        if (!this.hasModalTarget) return;
        this.modalTarget.style.display = 'block';
        if (this.hasModalTitleTarget) this.modalTitleTarget.textContent = title;
    }

    closeModal(event) {
        if (event) event.preventDefault();
        if (this.hasModalTarget) this.modalTarget.style.display = 'none';
        this.proposedExternalId = null;
    }

    renderModalBody(html) {
        if (this.hasModalBodyTarget) this.modalBodyTarget.innerHTML = html;
    }

    setModalFooter(html) {
        if (this.hasModalFooterTarget) this.modalFooterTarget.innerHTML = html;
    }

    setStatus(msg) {
        if (this.hasStatusMessageTarget) this.statusMessageTarget.textContent = msg;
    }

    getCSRFToken() {
        var meta = document.querySelector('meta[name="csrf-token"]');
        return meta ? meta.getAttribute("content") : "";
    }
}
